import { objectsAreEqualIgnoreUndefined } from '@sequencehq/utils'
import { omit } from 'lodash/fp'
import pick from 'lodash/fp/pick'

import { CubeEditorData } from 'modules/Cube/domain/cube.domain.types'
import { hasPresentationChanged } from '../QuoteEditorContent/utils/presentation'

export type ShouldSyncQuoteReasons = {
  cubeDataHasChanged: boolean
  presentationHasChanged: boolean
  /**
   * Which of the compared properties still match the previous data. It's a
   * function because working it out repeats the whole comparison a property at
   * a time, and the only thing that wants it is the developer log.
   */
  detailedEquality: () => Record<string, boolean>
}

/**
 * Whether the editor is holding changes the server hasn't been told about.
 *
 * `previous` is the data as of the last time we looked, so with nothing to
 * compare against there's nothing to sync yet.
 * `isPreviousDataUpToDateWithServer` says the editor hasn't reloaded since —
 * a reload replaces everything, and that isn't the user's edit.
 */
export const shouldSyncQuote = ({
  current,
  previous,
  isAlreadySaving,
  isPreviousDataUpToDateWithServer
}: {
  current: CubeEditorData
  previous: CubeEditorData | undefined
  isAlreadySaving: boolean
  isPreviousDataUpToDateWithServer: boolean
}): { needsSync: boolean; reasons: ShouldSyncQuoteReasons } => {
  const cubeDataHasChanged = !objectsAreEqualIgnoreUndefined(
    comparableProperties(current),
    comparableProperties(previous)
  )
  const presentationHasChanged = Boolean(
    previous &&
      hasPresentationChanged(previous.presentation, current.presentation)
  )

  return {
    needsSync:
      !isAlreadySaving &&
      Boolean(previous) &&
      isPreviousDataUpToDateWithServer &&
      (cubeDataHasChanged || presentationHasChanged),
    reasons: {
      cubeDataHasChanged,
      presentationHasChanged,
      detailedEquality: () => detailedEquality({ current, previous })
    }
  }
}

const comparableProperties = (data: CubeEditorData | undefined) =>
  pick(['common', 'phases', 'quote', 'discounts', 'minimums', 'prices'])({
    ...data,
    /**
     * The status should be entirely server driven, so do not account for it
     * when comparing local changes. Otherwise, changes such as 'optimistic updates'
     * can result in unnecessary syncs.
     */
    common: omit('status')(data?.common),
    quote: omit('integrationIds')(data?.quote)
  })

/**
 * Both sides go through `comparableProperties` first. Comparing the stripped
 * current data against the raw previous data would report `common` and `quote`
 * as different every single time, because the keys we just dropped are still on
 * the other side.
 */
const detailedEquality = ({
  current,
  previous
}: {
  current: CubeEditorData
  previous: CubeEditorData | undefined
}): Record<string, boolean> => {
  if (!previous) {
    return {}
  }

  const comparablePrevious = comparableProperties(previous)

  return Object.entries(comparableProperties(current)).reduce<
    Record<string, boolean>
  >(
    (acc, [property, value]) => ({
      ...acc,
      [property]: objectsAreEqualIgnoreUndefined(
        value,
        comparablePrevious[property as keyof CubeEditorData] ?? null
      )
    }),
    {}
  )
}
